/* istanbul ignore file */
import { inject, nextTick } from 'vue';
import useImages from '~/composables/images/useImages';

/**
 *
 * @param {Object} object
 * @param {import('vue').Ref<number>} object.currentPage
 * @param {import('vue').Ref<number>} object.prevIndex
 * @param {import('vue').Ref<number>} object.numberOfPages
 * @param {{
 *   childWidth: number,
 *   wrapWidth: number | null,
 *   itemsLength: number
 * }} object.dimensions
 * @param {import('vue').Ref<boolean>} object.noTransition
 * @param {import('vue').Ref<boolean>} object.sliding
 * @param {import('vue').Ref<number>} object.scrollerX
 * @param {import('vue').Ref<boolean>} object.hasBeenDragged
 * @param {import('vue').Ref<HTMLElement | null>} object.scroller
 * @param {(eventName: 'slideshow-change' | 'load' | 'update-first-alt-dot', ...args: any[]) => void} object.emit
 */
const useSlideshowNavigation = ({
  currentPage,
  prevIndex,
  numberOfPages,
  noTransition,
  sliding,
  scrollerX,
  hasBeenDragged,
  dimensions,
  scroller,
  emit,
}) => {
  const { carouselImages } = useImages();
  const triggerLastAltImageClickOnce = inject('triggerLastAltImageClickOnce', () => {});

  /**
   * @param {number} [activePage]
   */
  const scrollerPosition = (activePage) => -(dimensions.childWidth * (activePage !== undefined ? activePage : currentPage.value));

  const slotsItems = () => {
    if (scroller.value) {
      return [...scroller.value.getElementsByClassName('slideshow-item')];
    }
    return [];
  };

  /**
   * @param {number} index
   */
  const checkImgLoaded = (index) => {
    const nextItem = slotsItems()[index];
    if (!nextItem) {
      return true;
    }
    const imgs = nextItem.getElementsByTagName('img');
    let completed = true;
    if (imgs && imgs.length) {
      [...imgs].forEach((img) => {
        img.addEventListener('load', () => {
          if (!img.complete) completed = false;
        });
      });
    }
    return completed;
  };

  /**
   * @param {number} flag
   */
  const move = (flag) => {
    if (!checkImgLoaded(currentPage.value + flag)) return false;
    if (sliding.value) return false;

    sliding.value = true;
    if ((flag < 0 && currentPage.value > 1) || (flag > 0 && currentPage.value < numberOfPages.value)) {
      prevIndex.value = currentPage.value;
      currentPage.value += flag;
      scrollerX.value = scrollerPosition(prevIndex.value);

      emit('slideshow-change', currentPage.value);

      setTimeout(() => {
        sliding.value = false;
        prevIndex.value = -1;
        hasBeenDragged.value = false;
      }, 200);
    } else {
      prevIndex.value = currentPage.value + flag;
      scrollerX.value = scrollerPosition(currentPage.value + flag);

      emit('slideshow-change', currentPage.value);

      setTimeout(() => {
        currentPage.value = flag > 0 ? 1 : numberOfPages.value;

        noTransition.value = true;
        scrollerX.value = scrollerPosition();
        prevIndex.value = -1;

        emit('slideshow-change', currentPage.value);

        setTimeout(() => {
          noTransition.value = false;
          sliding.value = false;
          hasBeenDragged.value = false;
        }, 200);
      }, 200);
    }

    return true;
  };

  /**
   * @param {number} index
   */
  const change = (index) => {
    if (!checkImgLoaded(index)) return;

    currentPage.value = index;
    if (currentPage.value === carouselImages.value.length) {
      triggerLastAltImageClickOnce();
    }
    scrollerX.value = scrollerPosition(index - 1);
    emit('slideshow-change', currentPage.value);
  };

  const changeAsync = async (index) => {
    if (!checkImgLoaded(index)) {
      return;
    }

    if (sliding.value) {
      return;
    }

    const newScrollerX = scrollerPosition(index - 1);
    if (newScrollerX === scrollerX.value) {
      return Promise.resolve(currentPage.value);
    }
    sliding.value = true;
    currentPage.value = index;

    if (currentPage.value === carouselImages.value.length) {
      triggerLastAltImageClickOnce();
    }

    scrollerX.value = newScrollerX;
    await nextTick();
    emit('slideshow-change', currentPage.value);

    return new Promise((resolve) => {
      const handleTransitionEnd = () => {
        sliding.value = false;
        resolve(currentPage.value);
      };

      scroller.value.addEventListener('transitionend', handleTransitionEnd, { once: true });
    });
  };

  return {
    move,
    change,
    scrollerPosition,
    changeAsync,
  };
};

export default useSlideshowNavigation;
